import { layoutRect, layoutCoordinate, viewportHeight as layoutViewportHeight } from "../utils/displayDensity";
/* eslint-disable */
import * as d3 from "d3";
import { Maximize2, Search, PlayCircle, X as CloseIcon } from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import TextInputField from "../components/Fields/TextInputField";
import getVideoEmbed from "./mindSHARE/utils/getVideoEmbed";

import { API_BASE as YOUTUBE_API_BASE } from "../config/api";

async function fetchYouTubeVideos(query, signal, offset = 0) {
  const params = new URLSearchParams();
  const q = String(query || "").trim();
  if (q) params.set("q", q);
  params.set("offset", String(offset));

  const suffix = params.toString() ? `?${params.toString()}` : "";
  const res = await fetch(`${YOUTUBE_API_BASE}/youtube/videos${suffix}`, {
    cache: "no-store",
    signal,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`YouTube videos request failed: ${res.status} ${text}`);
  }

  return res.json();
}

async function fetchYouTubeTimeline(query, signal) {
  const q = String(query || "").trim();
  if (!q) return { query: "", total: 0, total_views: 0, videos: [] };

  const params = new URLSearchParams({ q });
  const res = await fetch(`${YOUTUBE_API_BASE}/youtube/timeline?${params.toString()}`, {
    cache: "no-store",
    signal,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`YouTube timeline request failed: ${res.status} ${text}`);
  }

  return res.json();
}

async function fetchYouTubeThisWeek(signal) {
  const res = await fetch(`${YOUTUBE_API_BASE}/youtube/timeline/this-week`, {
    cache: "no-store",
    signal,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`YouTube this week request failed: ${res.status} ${text}`);
  }

  return res.json();
}

const TimelineVideoMark = memo(function TimelineVideoMark({ kind, x, y, width, height, seriesClass, active }) {
  const suffix = `${seriesClass || "primary"}${active ? " active" : ""}`;
  return kind === "bars"
    ? <rect x={x - width / 2} y={y} width={width} height={height} rx={Math.min(3, width / 2)} className={`mg-youtube-bar ${suffix}`} />
    : <circle cx={x} cy={y} r={active ? 5 : 3.3} className={`mg-youtube-cumulative-point ${suffix}`} />;
});

function fmtInt(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return new Intl.NumberFormat("en-US").format(Math.round(n));
}

function fmtCompact(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return fmtInt(n);
}

function fmtDate(value) {
  if (!value) return "Unknown date";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Unknown date";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getBestThumbnailUrl(video) {
  const id = String(video?.video_id || "").trim();
  if (id) return `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;
  return video?.thumbnail_url || "";
}

function VideoCard({ video, index, onExpand }) {
  const href = video.youtube_url || `https://www.youtube.com/watch?v=${video.video_id}`;
  const embed = getVideoEmbed(href);
  const controlledVideoSrc = embed?.type === "youtube"
    ? embed.src.replace("controls=0", "controls=1")
    : embed?.src;
  const [showVideoPreview, setShowVideoPreview] = useState(false);

  useEffect(() => {
    setShowVideoPreview(false);
  }, [video.video_id, href]);

  return (
    <article
      className="mg-youtube-card"
      aria-label={video.title || "YouTube video"}
    >
      <div className="mg-youtube-thumb-wrap">
        {showVideoPreview && controlledVideoSrc ? (
          <iframe
            title={`${video.title || "YouTube"} preview`}
            src={controlledVideoSrc}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="mg-youtube-frame"
          />
        ) : (
          video.thumbnail_url ? (
            <img
              src={video.thumbnail_url}
              alt={video.title || "YouTube thumbnail"}
              loading="lazy"
              decoding="async"
              className="mg-youtube-thumb"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <div className="mg-youtube-thumb-fallback">No thumbnail</div>
          )
        )}

        <div className="mg-youtube-rank">#{index + 1}</div>
        {showVideoPreview && controlledVideoSrc ? (
          <button
            type="button"
            className="mg-youtube-expand"
            onClick={() => onExpand(video)}
            aria-label="Expand video"
            title="Expand video"
          >
            <Maximize2 size={18} strokeWidth={2.2} />
          </button>
        ) : (
          <button
            type="button"
            className="mg-youtube-play"
            onClick={() => setShowVideoPreview(true)}
            aria-label={`Play ${video.title || "YouTube video"}`}
          >
            <PlayCircle size={26} />
          </button>
        )}
      </div>

      <div className="mg-youtube-card-body">
        <div className="mg-youtube-title" title={video.title || ""}>
          {video.title || "Untitled video"}
        </div>
        <div className="mg-youtube-channel" title={video.channel_handle || video.channel_name || ""}>
          {video.channel_name || "Unknown channel"}
        </div>
        <div className="mg-youtube-meta-row">
          <span>{fmtCompact(video.views)} views</span>
          <span>{fmtDate(video.published_at)}</span>
        </div>
      </div>
    </article>
  );
}

function VideoSkeletonGrid() {
  return (
    <div className="mg-youtube-grid" aria-hidden="true">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="mg-youtube-skel-card">
          <div className="mg-youtube-skel-thumb" />
          <div className="mg-youtube-skel-lines">
            <div className="mg-youtube-skel-line wide" />
            <div className="mg-youtube-skel-line" />
            <div className="mg-youtube-skel-line short" />
          </div>
        </div>
      ))}
    </div>
  );
}

function TimelineSkeleton() {
  return (
    <div className="mg-youtube-timeline-panel" aria-hidden="true">
      <div className="mg-youtube-timeline-skel-axis" />
      <div className="mg-youtube-timeline-skel-bars">
        {Array.from({ length: 42 }).map((_, i) => (
          <div
            key={i}
            className="mg-youtube-timeline-skel-bar"
            style={{ height: `${28 + ((i * 37) % 72)}%` }}
          />
        ))}
      </div>
      <div className="mg-youtube-timeline-skel-bottom" />
    </div>
  );
}

function YouTubeTimeline({
  videos,
  loading,
  error,
  query,
  total,
  compareQuery,
  comparePayload,
  compareLoading,
  compareError,
  onCompareChange,
  showCompareInput = true,
  chartHeight = 760,
  onResizeStart,
  onExpand,
  windowRange,
  onWindowChange,
}) {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(1100);
  const [hovered, setHovered] = useState(null);
  const [chartKind, setChartKind] = useState("bars");

  useEffect(() => {
    if (!showCompareInput && chartKind !== "bars") {
      setChartKind("bars");
    }
  }, [showCompareInput, chartKind]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const update = () => setWidth(Math.max(360, Math.floor(layoutRect(el).width)));
    update();
    requestAnimationFrame(update);
    const settleTimer = setTimeout(update, 120);

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => {
        clearTimeout(settleTimer);
        window.removeEventListener("resize", update);
      };
    }

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      clearTimeout(settleTimer);
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    const update = () => {
      const el = wrapRef.current;
      if (!el) return;
      setWidth(Math.max(360, Math.floor(layoutRect(el).width)));
    };
    update();
    requestAnimationFrame(update);
  }, [videos.length, comparePayload?.videos?.length, loading, compareLoading]);

  const allRows = useMemo(() => {
    const primaryRows = (videos || [])
      .map((video, index) => {
        const date = new Date(video.published_at);
        const views = Number(video.views || 0);
        if (Number.isNaN(date.getTime()) || !Number.isFinite(views)) return null;
        return { ...video, date, views, originalIndex: index, seriesKey: "primary", seriesLabel: query, seriesClass: "primary" };
      })
      .filter(Boolean);

    const compareRows = (comparePayload?.videos || [])
      .map((video, index) => {
        const date = new Date(video.published_at);
        const views = Number(video.views || 0);
        if (Number.isNaN(date.getTime()) || !Number.isFinite(views)) return null;
        return { ...video, date, views, originalIndex: index, seriesKey: "compare", seriesLabel: comparePayload?.query || compareQuery, seriesClass: "compare" };
      })
      .filter(Boolean);

    return [...primaryRows, ...compareRows]
      .sort((a, b) => a.date - b.date || a.seriesKey.localeCompare(b.seriesKey) || String(a.video_id || "").localeCompare(String(b.video_id || "")));
  }, [videos, query, comparePayload, compareQuery]);

  const compareTotal = Number(comparePayload?.total || 0);
  const compareTotalViews = Number(comparePayload?.total_views || 0);
  const hasCompare = !!String(compareQuery || "").trim();

  const chart = useMemo(() => {
    const n = allRows.length;
    const start = Math.max(0, Math.min(Number(windowRange?.start) || 0, Math.max(0, n - 1)));
    const end = Math.max(start, Math.min(Number(windowRange?.end) || Math.max(0, n - 1), Math.max(0, n - 1)));
    const rows = allRows.slice(start, end + 1).map((d) => ({ ...d }));

    const cumulativeBySeries = new Map();
    for (const d of rows) {
      const prev = cumulativeBySeries.get(d.seriesKey) || 0;
      const next = prev + d.views;
      cumulativeBySeries.set(d.seriesKey, next);
      d.cumulativeViews = next;
    }

    const height = Math.max(520, Math.min(980, Number(chartHeight) || 760));
    const margin = { top: 82, right: 22, bottom: 62, left: width < 640 ? 56 : 74 };
    const innerW = Math.max(1, width - margin.left - margin.right);
    const innerH = Math.max(1, height - margin.top - margin.bottom);
    const dates = rows.map((d) => d.date);
    const maxViews = d3.max(rows, (d) => chartKind === "cumulative" ? d.cumulativeViews : d.views) || 1;
    let domain = d3.extent(dates);

    if (!domain[0] || !domain[1]) {
      domain = [new Date(), new Date()];
    }
    if (+domain[0] === +domain[1]) {
      domain = [d3.timeDay.offset(domain[0], -1), d3.timeDay.offset(domain[1], 1)];
    }

    const x = d3.scaleTime().domain(domain).range([margin.left, width - margin.right]).nice();
    const y = d3.scaleLinear().domain([0, maxViews]).nice(5).range([height - margin.bottom, margin.top]);
    const spanDays = Math.max(1, (domain[1] - domain[0]) / 86400000);

    // Pick an interval + format that fits the available pixel width so labels never collide.
    const tickFormat = spanDays <= 3
      ? d3.timeFormat("%b %-d, %-I%p")
      : spanDays <= 120
      ? d3.timeFormat("%b %-d")
      : spanDays <= 730
      ? d3.timeFormat("%b '%y")
      : d3.timeFormat("%Y");
    const labelPx = spanDays <= 3 ? 96 : spanDays <= 120 ? 66 : spanDays <= 730 ? 64 : 48;
    const maxTicks = Math.max(2, Math.floor(innerW / labelPx));
    const candidateIntervals = [
      d3.timeHour.every(6), d3.timeHour.every(12), d3.timeDay.every(1), d3.timeDay.every(2),
      d3.timeWeek.every(1), d3.timeWeek.every(2), d3.timeMonth.every(1), d3.timeMonth.every(2),
      d3.timeMonth.every(3), d3.timeMonth.every(6), d3.timeYear.every(1), d3.timeYear.every(2),
      d3.timeYear.every(5),
    ].filter(Boolean);
    let ticksX = x.ticks(maxTicks);
    for (const interval of candidateIntervals) {
      const candidate = x.ticks(interval);
      if (candidate.length && candidate.length <= maxTicks) {
        ticksX = candidate;
        break;
      }
    }
    const ticksY = y.ticks(5);
    const visibleBarW = Math.max(5, Math.min(13, innerW / Math.max(rows.length, 1) * 1.05));

    const placed = rows.map((d) => ({ ...d, anchorX: x(d.date) }));
    const lanes = [];
    for (const d of placed) {
      let lane = 0;
      while (lanes[lane] !== undefined && d.anchorX - lanes[lane] < 16) lane += 1;
      lanes[lane] = d.anchorX;
      const offset = lane === 0 ? 0 : (lane % 2 ? 1 : -1) * Math.ceil(lane / 2) * Math.min(14, visibleBarW + 8);
      d.x = Math.max(margin.left, Math.min(width - margin.right, d.anchorX + offset));
    }

    const bySeriesDate = new Map();
    for (const d of placed) {
      const key = `${d.seriesKey}:${+d.date}`;
      const ordinal = bySeriesDate.get(key) || 0;
      bySeriesDate.set(key, ordinal + 1);
      d.cumulativeX = d.anchorX + ordinal * 0.001;
    }

    const topVideoIds = new Set(
      [...placed]
        .sort((a, b) => b.views - a.views)
        .slice(0, 5)
        .map((d) => `${d.seriesKey}:${d.video_id}`)
    );

    const area = d3.area()
      .x((d) => d.cumulativeX)
      .y0(height - margin.bottom)
      .y1((d) => y(d.cumulativeViews || 0))
      .curve(d3.curveMonotoneX);

    const line = d3.line()
      .x((d) => d.cumulativeX)
      .y((d) => y(d.cumulativeViews || 0))
      .curve(d3.curveMonotoneX);

    const areaSeries = Array.from(d3.group(placed, (d) => d.seriesKey), ([seriesKey, seriesRows]) => {
      const sortedRows = [...seriesRows].sort((a, b) => a.date - b.date || String(a.video_id || "").localeCompare(String(b.video_id || "")));
      return {
        seriesKey,
        seriesClass: sortedRows[0]?.seriesClass || seriesKey,
        label: sortedRows[0]?.seriesLabel || seriesKey,
        rows: sortedRows,
        areaPath: sortedRows.length ? area(sortedRows) : "",
        linePath: sortedRows.length ? line(sortedRows) : "",
      };
    });

    // Float the top-5 thumbnails just above their points, inside the plot area,
    // nudging them only as far as needed to clear each other and the data line.
    const calloutW = width < 700 ? 92 : 132;
    const calloutH = calloutW * 9 / 16;
    const calloutGap = 10;
    const leaderGap = 46;
    const plotTop = margin.top + 6;
    const minX = margin.left + calloutW / 2;
    const maxX = width - margin.right - calloutW / 2;
    const callouts = placed
      .filter((d) => topVideoIds.has(`${d.seriesKey}:${d.video_id}`) && getBestThumbnailUrl(d))
      .map((d) => {
        const pointX = chartKind === "cumulative" ? d.cumulativeX : d.x;
        const pointY = y(chartKind === "cumulative" ? d.cumulativeViews : d.views);
        return { video: d, pointX, pointY, thumbnailUrl: getBestThumbnailUrl(d) };
      })
      .sort((a, b) => a.pointX - b.pointX);

    const settled = [];
    for (const c of callouts) {
      c.width = calloutW;
      c.height = calloutH;
      c.boxCenterX = Math.max(minX, Math.min(maxX, c.pointX));
      c.boxY = Math.max(plotTop, c.pointY - leaderGap - calloutH);

      // Stack upward first; only slide sideways once there is no vertical room left.
      for (let guard = 0; guard < 12; guard++) {
        const hit = settled.find(
          (o) =>
            Math.abs(o.boxCenterX - c.boxCenterX) < calloutW + calloutGap &&
            c.boxY < o.boxY + calloutH + calloutGap &&
            c.boxY + calloutH + calloutGap > o.boxY
        );
        if (!hit) break;
        const lifted = hit.boxY - calloutH - calloutGap;
        if (lifted >= plotTop) {
          c.boxY = lifted;
        } else {
          c.boxCenterX = Math.min(maxX, hit.boxCenterX + calloutW + calloutGap);
          c.boxY = Math.max(plotTop, c.pointY - leaderGap - calloutH);
        }
      }

      c.boxX = c.boxCenterX - calloutW / 2;
      c.anchorY = c.boxY + calloutH;
      c.leaderPath = `M ${c.boxCenterX} ${c.anchorY} L ${c.boxCenterX} ${c.anchorY + 8} L ${c.pointX} ${c.pointY}`;
      settled.push(c);
    }

    return { rows: placed, width, height, margin, x, y, ticksX, ticksY, tickFormat, visibleBarW, topVideoIds, areaSeries, callouts, start, end, n };
  }, [allRows, windowRange, width, chartHeight, chartKind]);

  const sliderMax = Math.max(0, allRows.length - 1);
  const sliderStart = Math.max(0, Math.min(Number(windowRange?.start) || 0, sliderMax));
  const sliderEnd = Math.max(sliderStart, Math.min(Number(windowRange?.end) || sliderMax, sliderMax));
  const sliderRange = Math.max(1, sliderMax);
  const startPct = (100 * sliderStart) / sliderRange;
  const endPct = (100 * sliderEnd) / sliderRange;
  const formatSliderDate = (idx) => fmtDate(allRows[idx]?.published_at || allRows[idx]?.date);

  const pickNearestVideo = (event) => {
    if (!chart.rows.length) return null;
    const svgRect = layoutRect(event.currentTarget.ownerSVGElement);
    const px = (layoutCoordinate(event.clientX) - svgRect.left) * (chart.width / Math.max(1, svgRect.width));
    const py = (layoutCoordinate(event.clientY) - svgRect.top) * (chart.height / Math.max(1, svgRect.height));
    const baseline = chart.height - chart.margin.bottom;
    const horizontalRadius = Math.max(18, chart.visibleBarW + 14);

    let candidates = chart.rows
      .map((video) => {
        const pointY = chart.y(chartKind === "cumulative" ? video.cumulativeViews : video.views);
        const pointX = chartKind === "cumulative" ? video.cumulativeX : video.x;
        const dx = Math.abs(pointX - px);
        const insideY = chartKind === "cumulative" ? Math.abs(py - pointY) <= 46 : py >= pointY - 28 && py <= baseline + 10;
        const verticalPenalty = insideY ? 0 : Math.min(Math.abs(py - pointY), Math.abs(py - baseline)) * 0.25;
        return { video, dx, score: dx * 3 + verticalPenalty };
      })
      .filter((d) => d.dx <= horizontalRadius)
      .sort((a, b) => a.score - b.score || b.video.views - a.video.views);

    if (!candidates.length) {
      candidates = chart.rows
        .map((video) => ({ video, dx: Math.abs((chartKind === "cumulative" ? video.cumulativeX : video.x) - px) }))
        .sort((a, b) => a.dx - b.dx || b.video.views - a.video.views);
    }

    return candidates[0]?.video || null;
  };

  const handleTimelineMove = (event) => {
    const video = pickNearestVideo(event);
    if (!video) return;

    const svgRect = layoutRect(event.currentTarget.ownerSVGElement);
    const panelRect = wrapRef.current?.getBoundingClientRect();
    const scaleX = svgRect.width / Math.max(1, chart.width);
    const scaleY = svgRect.height / Math.max(1, chart.height);
    const anchorX = svgRect.left - (panelRect?.left || 0) + (chartKind === "cumulative" ? video.cumulativeX : video.x) * scaleX;
    const anchorY = svgRect.top - (panelRect?.top || 0) + chart.y(chartKind === "cumulative" ? video.cumulativeViews : video.views) * scaleY;
    const tooltipW = Math.min(500, Math.max(320, (panelRect?.width || 500) - 28));
    const tooltipH = 174;
    const left = anchorX + tooltipW + 26 > (panelRect?.width || 0)
      ? Math.max(12, anchorX - tooltipW - 18)
      : anchorX + 18;
    const top = Math.max(46, Math.min(anchorY - 74, Math.max(46, (panelRect?.height || 560) - tooltipH - 18)));

    setHovered((previous) => previous?.video_id === video.video_id && previous?.seriesKey === video.seriesKey
      ? previous : { ...video, tooltipLeft: left, tooltipTop: top, tooltipWidth: tooltipW });
  };

  const handleTimelineClick = (event) => {
    const video = pickNearestVideo(event);
    if (video) onExpand(video);
  };

  if (loading) return <TimelineSkeleton />;

  if (error) {
    return (
      <div className="mg-youtube-state" role="alert">
        <div>
          <div className="mg-youtube-state-title">Could not load timeline</div>
          <div className="mg-youtube-state-copy">{error}</div>
        </div>
      </div>
    );
  }

  if (!query) {
    return (
      <div className="mg-youtube-state">
        <div>
          <div className="mg-youtube-state-title">Search to build a timeline</div>
          <div className="mg-youtube-state-copy">Timeline uses every matching video for the searched title text.</div>
        </div>
      </div>
    );
  }

  if (!chart.rows.length) {
    return (
      <div className="mg-youtube-state">
        <div>
          <div className="mg-youtube-state-title">No timeline videos found</div>
          <div className="mg-youtube-state-copy">Try another title search to broaden the historical footprint.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mg-youtube-timeline-panel" ref={wrapRef}>
      <div className="mg-youtube-timeline-note">
        Showing {fmtInt(chart.rows.length)} visible observations across the selected timeline window.
      </div>
      <div className="mg-youtube-timeline-compare-row">
        <div className="mg-youtube-timeline-legend" aria-label="Timeline series">
          <span><i className="primary" /> {query} ({fmtInt(total)})</span>
          {hasCompare && (
            <span><i className="compare" /> {comparePayload?.query || compareQuery} ({compareLoading ? "..." : fmtInt(compareTotal)})</span>
          )}
        </div>
        <div className="mg-youtube-timeline-tools">
          {showCompareInput ? (
            <div className="mg-youtube-chart-kind-toggle" role="tablist" aria-label="Timeline chart type">
              <button
                type="button"
                className={chartKind === "bars" ? "active" : ""}
                onClick={() => setChartKind("bars")}
                aria-selected={chartKind === "bars"}
              >
                Bars
              </button>
              <button
                type="button"
                className={chartKind === "cumulative" ? "active" : ""}
                onClick={() => setChartKind("cumulative")}
                aria-selected={chartKind === "cumulative"}
              >
                Cumulative
              </button>
            </div>
          ) : null}
          {showCompareInput ? (
            <div className="mg-youtube-compare-input-wrap">
              <Search className="mg-youtube-compare-icon" size={14} aria-hidden="true" />
              <TextInputField
                value={compareQuery}
                onChange={(e) => onCompareChange(e.target.value)}
                placeholder="Add second search term..."
                width="100%"
                aria-label="Add second YouTube timeline search term"
                style={{
                  height: 32,
                  paddingLeft: 31,
                  borderRadius: 8,
                  background: "rgba(0,0,0,0.44)",
                  borderColor: "rgba(255,255,255,0.13)",
                  fontSize: 12,
                  fontWeight: 800,
                }}
              />
            </div>
          ) : null}
        </div>
      </div>
      {compareError && <div className="mg-youtube-compare-error">{compareError}</div>}
      <svg
        className="mg-youtube-timeline-svg"
        width={chart.width}
        height={chart.height}
        style={{ height: chart.height }}
        role="img"
        aria-label={`Timeline for ${query}`}
      >
        <defs>
          <linearGradient id="mgYoutubeBarFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,72,72,0.98)" />
            <stop offset="68%" stopColor="rgba(214,20,20,0.72)" />
            <stop offset="100%" stopColor="rgba(120,9,9,0.52)" />
          </linearGradient>
          <linearGradient id="mgYoutubeWatermarkFade" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#fff" stopOpacity="0.25" />
          </linearGradient>
          <mask id="mgYoutubeWatermarkMask">
            <rect
              x={chart.margin.left + 14}
              y={chart.margin.top + 12}
              width={215}
              height={68}
              fill="url(#mgYoutubeWatermarkFade)"
            />
          </mask>
        </defs>

        <g className="mg-youtube-watermark" mask="url(#mgYoutubeWatermarkMask)" aria-hidden="true">
          <image
            href={`${process.env.PUBLIC_URL}/YouTube Icon.png`}
            x={chart.margin.left + 14}
            y={chart.margin.top + 12}
            width={62}
            height={44}
            preserveAspectRatio="xMidYMid meet"
          />
          <text x={chart.margin.left + 84} y={chart.margin.top + 42} className="mg-youtube-watermark-text">
            YouTube
          </text>
        </g>

        {chart.ticksY.map((tick) => {
          const y = chart.y(tick);
          return (
            <g key={`y-${tick}`}>
              <line x1={chart.margin.left} x2={chart.width - chart.margin.right} y1={y} y2={y} className="mg-youtube-gridline" />
              <text x={chart.margin.left - 12} y={y + 4} textAnchor="end" className="mg-youtube-axis-label">
                {fmtCompact(tick)}
              </text>
            </g>
          );
        })}

        {chart.ticksX.map((tick) => {
          const x = chart.x(tick);
          return (
            <g key={`x-${+tick}`}>
              <line x1={x} x2={x} y1={chart.margin.top} y2={chart.height - chart.margin.bottom} className="mg-youtube-date-line" />
              <text x={x} y={chart.height - 26} textAnchor="middle" className="mg-youtube-axis-label mg-youtube-axis-label-date">
                {chart.tickFormat(tick)}
              </text>
            </g>
          );
        })}

        <line
          x1={chart.margin.left}
          x2={chart.width - chart.margin.right}
          y1={chart.height - chart.margin.bottom}
          y2={chart.height - chart.margin.bottom}
          className="mg-youtube-axis-line"
        />
        <line
          x1={chart.margin.left}
          x2={chart.margin.left}
          y1={chart.margin.top}
          y2={chart.height - chart.margin.bottom}
          className="mg-youtube-axis-line"
        />

        {chartKind === "cumulative" && chart.areaSeries.map((series) => (
          <g key={`area-${series.seriesKey}`} className={`mg-youtube-area-series ${series.seriesClass}`}>
            <path d={series.areaPath} className={`mg-youtube-area-fill ${series.seriesClass}`} />
            <path d={series.linePath} className={`mg-youtube-area-line ${series.seriesClass}`} />
          </g>
        ))}

        {chartKind === "bars" && chart.rows.map((video) => {
          const barTop = chart.y(video.views);
          const barHeight = Math.max(2, chart.height - chart.margin.bottom - barTop);
          const active = hovered?.video_id === video.video_id && hovered?.seriesKey === video.seriesKey;
          return (
            <TimelineVideoMark key={`${video.seriesKey}-${video.video_id || `${+video.date}-${video.originalIndex}`}`} kind="bars" x={video.x} y={barTop} width={chart.visibleBarW} height={barHeight} seriesClass={video.seriesClass} active={active} />
          );
        })}
        {chartKind === "cumulative" && chart.rows.map((video) => {
          const active = hovered?.video_id === video.video_id && hovered?.seriesKey === video.seriesKey;
          return (
            <TimelineVideoMark
              key={`point-${video.seriesKey}-${video.video_id || `${+video.date}-${video.originalIndex}`}`}
              kind="cumulative"
              x={video.cumulativeX}
              y={chart.y(video.cumulativeViews || 0)}
              seriesClass={video.seriesClass}
              active={active}
            />
          );
        })}
        {chart.callouts.map((callout) => {
          const video = callout.video;
          return (
            <g key={`thumb-${video.seriesKey}-${video.video_id}`} className="mg-youtube-top-thumb-marker">
              <path d={callout.leaderPath} className={`mg-youtube-callout-leader ${video.seriesClass || "primary"}`} />
              <circle
                cx={callout.pointX}
                cy={callout.pointY}
                r={4.6}
                className={`mg-youtube-callout-anchor ${video.seriesClass || "primary"}`}
              />
              <rect
                x={callout.boxX - 2}
                y={callout.boxY - 2}
                width={callout.width + 4}
                height={callout.height + 4}
                rx="5"
                className={`mg-youtube-top-thumb-frame ${video.seriesClass || "primary"}`}
              />
              <image
                href={callout.thumbnailUrl}
                x={callout.boxX}
                y={callout.boxY}
                width={callout.width}
                height={callout.height}
                preserveAspectRatio="xMidYMid slice"
                className="mg-youtube-top-thumb-img"
              />
            </g>
          );
        })}
        <rect
          className="mg-youtube-timeline-hitplane"
          x={chart.margin.left}
          y={chart.margin.top}
          width={chart.width - chart.margin.left - chart.margin.right}
          height={chart.height - chart.margin.top - chart.margin.bottom}
          fill="transparent"
          onMouseMove={handleTimelineMove}
          onMouseLeave={() => setHovered(null)}
          onClick={handleTimelineClick}
        />
      </svg>

      {hovered && (
        <div
          className="mg-youtube-timeline-tooltip"
          style={{ left: hovered.tooltipLeft, top: hovered.tooltipTop, width: hovered.tooltipWidth }}
        >
          <div className="mg-youtube-tooltip-thumb">
            {hovered.thumbnail_url ? (
              <img src={hovered.thumbnail_url} alt="" loading="lazy" />
            ) : null}
          </div>
          <div className="mg-youtube-tooltip-body">
            <div className={`mg-youtube-tooltip-series ${hovered.seriesClass || "primary"}`}>{hovered.seriesLabel || query}</div>
            <div className="mg-youtube-tooltip-title">{hovered.title || "Untitled video"}</div>
            <div className="mg-youtube-tooltip-channel">{hovered.channel_name || "Unknown channel"}</div>
            <div className="mg-youtube-tooltip-meta">
              {fmtDate(hovered.published_at)} · {fmtInt(hovered.views)} lifetime views
            </div>
          </div>
        </div>
      )}

      <div className="mg-youtube-period-slider-wrap">
        <div className="mg-youtube-period-label-row">
          <span>{formatSliderDate(sliderStart)}</span>
          <span>Timeline window</span>
          <span>{formatSliderDate(sliderEnd)}</span>
        </div>
        <div className="mg-youtube-mindshare-range-wrap">
          <div className="mg-youtube-mindshare-range-track" />
          <div
            className="mg-youtube-mindshare-range-fill"
            style={{ left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%` }}
          />
          <input
            className="mg-mindshare-range mg-youtube-range-input"
            type="range"
            min={0}
            max={sliderMax}
            value={sliderStart}
            onChange={(e) => {
              const nextStart = Math.min(parseInt(e.target.value, 10), sliderEnd - 1);
              onWindowChange({ start: nextStart, end: sliderEnd });
            }}
          />
          <input
            className="mg-mindshare-range mg-youtube-range-input"
            type="range"
            min={0}
            max={sliderMax}
            value={sliderEnd}
            onChange={(e) => {
              const nextEnd = Math.max(parseInt(e.target.value, 10), sliderStart + 1);
              onWindowChange({ start: sliderStart, end: nextEnd });
            }}
          />
        </div>
      </div>

      <button
        type="button"
        className="mg-youtube-resize-handle"
        onPointerDown={onResizeStart}
        title="Drag to resize chart"
        aria-label="Drag to resize chart"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
          <path d="M10 14 L14 10" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          <path d="M6 14 L14 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

function YouTube() {
  const [searchText, setSearchText] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [compareSearchText, setCompareSearchText] = useState("");
  const [debouncedCompareQuery, setDebouncedCompareQuery] = useState("");
  const [viewMode, setViewMode] = useState("cards");
  const [payload, setPayload] = useState({ total: 0, total_views: 0, videos: [], next_offset: null });
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState("");
  const [retryPage, setRetryPage] = useState(0);
  const loadMoreRef = useRef(null);
  const [timelinePayload, setTimelinePayload] = useState({ query: "", total: 0, total_views: 0, videos: [] });
  const [compareTimelinePayload, setCompareTimelinePayload] = useState({ query: "", total: 0, total_views: 0, videos: [] });
  const [thisWeekPayload, setThisWeekPayload] = useState({ query: "This Week", total: 0, total_views: 0, videos: [] });
  const [timelineWindow, setTimelineWindow] = useState({ start: 0, end: 0 });
  const [timelineChartHeight, setTimelineChartHeight] = useState(780);
  const [loading, setLoading] = useState(true);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [compareTimelineLoading, setCompareTimelineLoading] = useState(false);
  const [thisWeekLoading, setThisWeekLoading] = useState(false);
  const [error, setError] = useState("");
  const [timelineError, setTimelineError] = useState("");
  const [compareTimelineError, setCompareTimelineError] = useState("");
  const [thisWeekError, setThisWeekError] = useState("");
  const [expandedVideo, setExpandedVideo] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchText.trim()), 300);
    return () => clearTimeout(t);
  }, [searchText]);

  useEffect(() => {
    setCompareSearchText("");
    setDebouncedCompareQuery("");
    setCompareTimelinePayload({ query: "", total: 0, total_views: 0, videos: [] });
  }, [debouncedQuery]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedCompareQuery(compareSearchText.trim()), 300);
    return () => clearTimeout(t);
  }, [compareSearchText]);

  useEffect(() => {
    if (viewMode !== "cards" || searchText.trim() !== debouncedQuery) return undefined;
    const controller = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setError("");
        setLoadMoreError("");
        setLoadingMore(false);
        const data = await fetchYouTubeVideos(debouncedQuery, controller.signal);
        setPayload({
          total: Number(data?.total || 0),
          total_views: Number(data?.total_views || 0),
          videos: Array.isArray(data?.videos) ? data.videos : [],
          next_offset: data?.next_offset ?? null,
        });
      } catch (e) {
        if (e.name === "AbortError") return;
        console.error("Failed to load YouTube videos:", e);
        setError("Unable to load YouTube videos right now.");
        setPayload({ total: 0, total_views: 0, videos: [] });
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [debouncedQuery, viewMode, searchText]);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || viewMode !== "cards" || loading || searchText.trim() !== debouncedQuery || payload.next_offset == null) return undefined;
    const controller = new AbortController();
    let fetching = false;
    const observer = new IntersectionObserver(async (entries) => {
      if (!entries.some((entry) => entry.isIntersecting) || fetching) return;
      fetching = true;
      setLoadingMore(true);
      setLoadMoreError("");
      try {
        const data = await fetchYouTubeVideos(debouncedQuery, controller.signal, payload.next_offset);
        if (controller.signal.aborted) return;
        setLoadingMore(false);
        setPayload((previous) => {
          const ids = new Set(previous.videos.map((video) => video.video_id));
          return { ...previous, videos: [...previous.videos, ...(data.videos || []).filter((video) => !ids.has(video.video_id))], next_offset: data.next_offset ?? null };
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        setLoadingMore(false);
        setLoadMoreError("Unable to load more videos.");
      }
    }, { rootMargin: "700px" });
    observer.observe(sentinel);
    return () => { controller.abort(); observer.disconnect(); };
  }, [debouncedQuery, searchText, viewMode, loading, payload.next_offset, retryPage]);

  useEffect(() => {
    if (!debouncedQuery && viewMode === "timeline") {
      setViewMode("cards");
    }
  }, [debouncedQuery, viewMode]);

  useEffect(() => {
    if (viewMode !== "thisWeek") return;

    const controller = new AbortController();

    (async () => {
      try {
        setThisWeekLoading(true);
        setThisWeekError("");
        const data = await fetchYouTubeThisWeek(controller.signal);
        const thisWeekVideos = Array.isArray(data?.videos) ? data.videos : [];
        setThisWeekPayload({
          query: data?.query || "This Week",
          total: Number(data?.total || 0),
          total_views: Number(data?.total_views || 0),
          videos: thisWeekVideos,
        });
        setTimelineWindow({ start: 0, end: Math.max(0, thisWeekVideos.length - 1) });
      } catch (e) {
        if (e.name === "AbortError") return;
        console.error("Failed to load YouTube This Week timeline:", e);
        setThisWeekError("Unable to load This Week right now.");
        setThisWeekPayload({ query: "This Week", total: 0, total_views: 0, videos: [] });
      } finally {
        if (!controller.signal.aborted) setThisWeekLoading(false);
      }
    })();

    return () => controller.abort();
  }, [viewMode]);

  useEffect(() => {
    if (viewMode !== "timeline" || !debouncedQuery || searchText.trim() !== debouncedQuery) return;

    const controller = new AbortController();

    (async () => {
      try {
        setTimelineLoading(true);
        setTimelineError("");
        const data = await fetchYouTubeTimeline(debouncedQuery, controller.signal);
        const timelineVideos = Array.isArray(data?.videos) ? data.videos : [];
        setTimelinePayload({
          query: data?.query || debouncedQuery,
          total: Number(data?.total || 0),
          total_views: Number(data?.total_views || 0),
          videos: timelineVideos,
        });
        setTimelineWindow({ start: 0, end: Math.max(0, timelineVideos.length - 1) });
      } catch (e) {
        if (e.name === "AbortError") return;
        console.error("Failed to load YouTube timeline:", e);
        setTimelineError("Unable to load the YouTube timeline right now.");
        setTimelinePayload({ query: debouncedQuery, total: 0, total_views: 0, videos: [] });
      } finally {
        if (!controller.signal.aborted) setTimelineLoading(false);
      }
    })();

    return () => controller.abort();
  }, [viewMode, debouncedQuery, searchText]);

  useEffect(() => {
    if (viewMode !== "timeline" || !debouncedQuery || !debouncedCompareQuery) {
      setCompareTimelineLoading(false);
      setCompareTimelineError("");
      setCompareTimelinePayload({ query: "", total: 0, total_views: 0, videos: [] });
      return;
    }

    const controller = new AbortController();

    (async () => {
      try {
        setCompareTimelineLoading(true);
        setCompareTimelineError("");
        const data = await fetchYouTubeTimeline(debouncedCompareQuery, controller.signal);
        const compareVideos = Array.isArray(data?.videos) ? data.videos : [];
        setCompareTimelinePayload({
          query: data?.query || debouncedCompareQuery,
          total: Number(data?.total || 0),
          total_views: Number(data?.total_views || 0),
          videos: compareVideos,
        });
        setTimelineWindow({
          start: 0,
          end: Math.max(0, (timelinePayload.videos?.length || 0) + compareVideos.length - 1),
        });
      } catch (e) {
        if (e.name === "AbortError") return;
        console.error("Failed to load comparison YouTube timeline:", e);
        setCompareTimelineError("Unable to load the comparison timeline right now.");
        setCompareTimelinePayload({ query: debouncedCompareQuery, total: 0, total_views: 0, videos: [] });
      } finally {
        if (!controller.signal.aborted) setCompareTimelineLoading(false);
      }
    })();

    return () => controller.abort();
  }, [viewMode, debouncedQuery, debouncedCompareQuery]);

  useEffect(() => {
    if (!expandedVideo) return;
    const onKey = (e) => {
      if (e.key === "Escape") setExpandedVideo(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expandedVideo]);

  const handleTimelineResizeStart = (e) => {
    e.preventDefault();
    const startY = layoutCoordinate(e.clientY);
    const startHeight = timelineChartHeight;
    const maxHeight = Math.max(620, Math.min(1100, layoutViewportHeight() - 210));
    const minHeight = 520;

    const onMove = (event) => {
      const next = startHeight + layoutCoordinate(event.clientY) - startY;
      setTimelineChartHeight(Math.max(minHeight, Math.min(maxHeight, next)));
    };

    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp, { once: true });
  };

  const videos = payload.videos || [];
  const timelineVideos = timelinePayload.videos || [];
  const compareTimelineVideos = compareTimelinePayload.videos || [];
  const summaryPayload = viewMode === "thisWeek" ? thisWeekPayload : viewMode === "timeline" && debouncedQuery ? timelinePayload : payload;
  const searchPending = searchText.trim() !== debouncedQuery;
  const timelinePending = searchPending || timelineLoading || timelinePayload.query !== debouncedQuery;
  const summaryLoading = viewMode === "thisWeek" ? thisWeekLoading : viewMode === "timeline" ? timelinePending : loading || searchPending;
  const expandedHref = expandedVideo
    ? expandedVideo.youtube_url || `https://www.youtube.com/watch?v=${expandedVideo.video_id}`
    : "";
  const expandedEmbed = expandedHref ? getVideoEmbed(expandedHref) : null;
  const expandedVideoSrc = expandedEmbed?.type === "youtube"
    ? expandedEmbed.src.replace("controls=0", "controls=1")
    : expandedEmbed?.src;
  const hasSearch = debouncedQuery.length > 0;
  const showCompareStats = viewMode === "timeline" && !!debouncedCompareQuery;
  const resultLabel = useMemo(() => {
    if (!hasSearch) return "Top lifetime videos";
    return `Results for "${debouncedQuery}"`;
  }, [hasSearch, debouncedQuery]);

  return (
    <div className="mg-youtube-page">
      <style>{`
        @keyframes mgYoutubeShimmer {
          0% { background-position: -340px 0; }
          100% { background-position: 340px 0; }
        }

        .mg-youtube-page {
          min-height: calc(var(--mg-vh) * 100);
          background: #000;
          color: #fff;
          padding: 22px 22px 34px;
        }

        .mg-youtube-shell {
          max-width: 1720px;
          margin: 0 auto;
        }

        .mg-youtube-header {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(280px, 440px);
          gap: 18px;
          align-items: center;
          margin-bottom: 18px;
        }

        .mg-youtube-brand {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .mg-youtube-logo {
          width: 42px;
          height: 42px;
          object-fit: contain;
          filter: drop-shadow(0 8px 20px rgba(255, 0, 0, 0.22));
          flex: 0 0 auto;
        }

        .mg-youtube-kicker {
          color: #a6adba;
          font-size: 12px;
          font-weight: 850;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 4px;
        }

        .mg-youtube-h1 {
          margin: 0;
          color: #fff;
          font-size: 34px;
          line-height: 1.02;
          font-weight: 950;
          letter-spacing: 0;
        }

        .mg-youtube-search-panel {
          order: 2;
          position: relative;
          background: rgba(10, 10, 11, 0.76);
          border: 1px solid rgba(255, 255, 255, 0.10);
          border-radius: 10px;
          box-shadow: 0 18px 44px rgba(0, 0, 0, 0.38);
          padding: 12px;
        }

        .mg-youtube-search-icon {
          position: absolute;
          left: 25px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          pointer-events: none;
        }

        .mg-youtube-stats {
          order: 1;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
          margin-bottom: 0;
        }

        .mg-youtube-stats.compare {
          grid-template-columns: repeat(4, minmax(0, 1fr));
        }

        .mg-youtube-stat {
          background: linear-gradient(180deg, rgba(18,18,20,0.82), rgba(9,9,10,0.76));
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 10px;
          padding: 14px 16px;
          box-shadow: 0 12px 30px rgba(0,0,0,0.28);
        }

        .mg-youtube-stat.primary {
          border-color: rgba(255,72,72,0.28);
          box-shadow: 0 12px 30px rgba(0,0,0,0.28), inset 3px 0 0 rgba(255,72,72,0.78);
        }

        .mg-youtube-stat.compare {
          border-color: rgba(180,190,200,0.30);
          box-shadow: 0 12px 30px rgba(0,0,0,0.28), inset 3px 0 0 #9aa3ad;
        }

        .mg-youtube-stat-label {
          color: #8f98a7;
          font-size: 12px;
          font-weight: 800;
          margin-bottom: 5px;
        }

        .mg-youtube-stat-value {
          color: #fff;
          font-size: 28px;
          line-height: 1;
          font-weight: 950;
        }

        .mg-youtube-result-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          color: #aeb7c5;
          font-size: 13px;
          font-weight: 800;
          flex: 1;
          min-width: 260px;
        }

        .mg-youtube-result-bar strong {
          color: #fff;
          font-weight: 950;
        }

        .mg-youtube-results-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 14px;
          margin-bottom: 14px;
        }

        .mg-youtube-view-toggle {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px;
          border: 1px solid rgba(255,255,255,0.11);
          border-radius: 999px;
          background: rgba(0,0,0,0.34);
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.025);
          flex: 0 0 auto;
        }

        .mg-youtube-view-toggle button {
          height: 30px;
          border: 0;
          border-radius: 999px;
          padding: 0 13px;
          background: transparent;
          color: #cdd5df;
          font-size: 12px;
          font-weight: 900;
          cursor: pointer;
        }

        .mg-youtube-view-toggle button.active {
          background: #3fa9f5;
          color: #000;
        }

        .mg-youtube-view-toggle button:disabled {
          color: #5f6670;
          cursor: not-allowed;
        }

        .mg-youtube-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(292px, 1fr));
          gap: 16px;
          align-items: start;
        }

        .mg-youtube-card {
          display: block;
          min-width: 0;
          color: #fff;
          text-decoration: none;
          background: linear-gradient(180deg, rgba(16,16,18,0.96), rgba(8,8,9,0.96));
          border: 1px solid rgba(255,255,255,0.085);
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 12px 28px rgba(0,0,0,0.44);
          transition: transform 180ms cubic-bezier(.2,.8,.2,1), border-color 180ms ease, box-shadow 180ms ease;
        }

        .mg-youtube-card button {
          font: inherit;
        }

        .mg-youtube-card:hover {
          transform: translateY(-3px);
          border-color: rgba(255,255,255,0.18);
          box-shadow: 0 18px 42px rgba(0,0,0,0.54), 0 0 0 1px rgba(255,0,0,0.16);
        }

        .mg-youtube-thumb-wrap {
          position: relative;
          aspect-ratio: 16 / 9;
          background: #111;
          overflow: hidden;
        }

        .mg-youtube-thumb {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 220ms ease, filter 220ms ease;
        }

        .mg-youtube-card:hover .mg-youtube-thumb {
          transform: scale(1.025);
          filter: saturate(1.08) contrast(1.04);
        }

        .mg-youtube-thumb-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #777;
          font-size: 12px;
          font-weight: 800;
          background: linear-gradient(135deg, #111, #1b1b1d);
        }

        .mg-youtube-frame {
          width: 100%;
          height: 100%;
          border: 0;
          display: block;
          background: #000;
        }

        .mg-youtube-rank {
          position: absolute;
          top: 8px;
          left: 8px;
          height: 24px;
          min-width: 34px;
          padding: 0 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          background: rgba(0,0,0,0.66);
          border: 1px solid rgba(255,255,255,0.12);
          color: #fff;
          font-size: 12px;
          font-weight: 950;
          backdrop-filter: blur(5px);
        }

        .mg-youtube-play {
          position: absolute;
          right: 9px;
          bottom: 9px;
          width: 42px;
          height: 42px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          background: rgba(255, 0, 0, 0.86);
          border: 0;
          box-shadow: 0 10px 26px rgba(0,0,0,0.48);
          cursor: pointer;
          z-index: 3;
        }

        .mg-youtube-expand {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 36px;
          height: 36px;
          border: 1px solid rgba(255,255,255,0.34);
          border-radius: 8px;
          background: rgba(0,0,0,0.68);
          color: #fff;
          display: grid;
          place-items: center;
          cursor: pointer;
          backdrop-filter: blur(8px);
          box-shadow: 0 8px 22px rgba(0,0,0,0.45);
          z-index: 4;
        }

        .mg-youtube-expanded-backdrop {
          position: fixed;
          inset: 0;
          z-index: 21000;
          display: grid;
          place-items: center;
          padding: 40px;
          background: rgba(0,0,0,0.84);
          backdrop-filter: blur(10px);
        }

        .mg-youtube-expanded-player {
          position: relative;
          width: min(1120px, calc(var(--mg-vw) * 90));
          aspect-ratio: 16 / 9;
          overflow: hidden;
          border-radius: 14px;
          border: 1px solid rgba(255,255,255,0.20);
          background: #000;
          box-shadow: 0 34px 100px rgba(0,0,0,0.82), 0 0 45px rgba(63,169,245,0.10);
        }

        .mg-youtube-expanded-close {
          position: absolute;
          top: 12px;
          right: 12px;
          width: 38px;
          height: 38px;
          border: 1px solid rgba(255,255,255,0.34);
          border-radius: 8px;
          background: rgba(0,0,0,0.72);
          color: #fff;
          display: grid;
          place-items: center;
          cursor: pointer;
          backdrop-filter: blur(8px);
        }

        .mg-youtube-resize-handle {
          position: absolute;
          right: 8px;
          bottom: 8px;
          width: 26px;
          height: 26px;
          border: none;
          background: transparent;
          color: #9aa3ad;
          cursor: ns-resize;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          z-index: 8;
          padding: 0;
          opacity: 0.95;
        }

        .mg-youtube-card-body {
          padding: 11px 12px 12px;
        }

        .mg-youtube-title {
          color: #fff;
          font-size: 14px;
          line-height: 1.24;
          font-weight: 900;
          min-height: 35px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .mg-youtube-channel {
          margin-top: 7px;
          color: #c9d1dd;
          font-size: 12px;
          font-weight: 800;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .mg-youtube-meta-row {
          margin-top: 8px;
          display: flex;
          justify-content: space-between;
          gap: 10px;
          color: #8993a3;
          font-size: 12px;
          font-weight: 800;
        }

        .mg-youtube-meta-row span {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .mg-youtube-skel-card {
          border-radius: 10px;
          overflow: hidden;
          background: rgba(15,15,16,0.94);
          border: 1px solid rgba(255,255,255,0.07);
        }

        .mg-youtube-skel-thumb,
        .mg-youtube-skel-line {
          background-image: linear-gradient(90deg, rgba(255,255,255,0.055), rgba(255,255,255,0.12), rgba(255,255,255,0.055));
          background-size: 340px 100%;
          animation: mgYoutubeShimmer 1.35s linear infinite;
        }

        .mg-youtube-skel-thumb {
          aspect-ratio: 16 / 9;
        }

        .mg-youtube-skel-lines {
          padding: 12px;
        }

        .mg-youtube-skel-line {
          height: 13px;
          border-radius: 999px;
          margin-bottom: 9px;
          width: 68%;
        }

        .mg-youtube-skel-line.wide { width: 94%; }
        .mg-youtube-skel-line.short { width: 42%; margin-bottom: 0; }

        .mg-youtube-timeline-panel {
          position: relative;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 10px;
          background: linear-gradient(180deg, rgba(16,16,18,0.92), rgba(7,7,8,0.94));
          box-shadow: 0 18px 46px rgba(0,0,0,0.40);
          padding: 16px 16px 10px;
          overflow: hidden;
        }

        .mg-youtube-timeline-note {
          color: #aeb7c5;
          font-size: 12px;
          font-weight: 850;
          margin-bottom: 4px;
        }

        .mg-youtube-timeline-compare-row {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(240px, 360px);
          gap: 12px;
          align-items: center;
          margin-bottom: 2px;
        }

        .mg-youtube-timeline-legend {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 10px 16px;
          color: #cfd8e3;
          font-size: 12px;
          font-weight: 850;
          min-width: 0;
        }

        .mg-youtube-timeline-legend span {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          min-width: 0;
        }

        .mg-youtube-timeline-legend i {
          width: 20px;
          height: 8px;
          border-radius: 999px;
          display: inline-block;
          box-shadow: 0 0 10px rgba(0,0,0,0.4);
        }

        .mg-youtube-timeline-legend i.primary {
          background: rgba(255,72,72,0.92);
        }

        .mg-youtube-timeline-legend i.compare {
          background: #9aa3ad;
        }

        .mg-youtube-timeline-tools {
          display: grid;
          grid-template-columns: auto minmax(200px, 1fr);
          gap: 10px;
          align-items: center;
          min-width: 0;
        }

        .mg-youtube-chart-kind-toggle {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px;
          border: 1px solid rgba(255,255,255,0.11);
          border-radius: 999px;
          background: rgba(0,0,0,0.34);
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.025);
          width: max-content;
        }

        .mg-youtube-chart-kind-toggle button {
          height: 26px;
          border: 0;
          border-radius: 999px;
          padding: 0 10px;
          background: transparent;
          color: #cdd5df;
          font-size: 11px;
          font-weight: 900;
          cursor: pointer;
        }

        .mg-youtube-chart-kind-toggle button.active {
          background: #9aa3ad;
          color: #050505;
        }

        .mg-youtube-compare-input-wrap {
          position: relative;
          min-width: 0;
        }

        .mg-youtube-compare-icon {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #9aa3ad;
          z-index: 2;
          pointer-events: none;
        }

        .mg-youtube-compare-error {
          color: #fca5a5;
          font-size: 12px;
          font-weight: 800;
          margin: -2px 0 8px;
        }

        .mg-youtube-timeline-svg {
          display: block;
          width: 100%;
          height: auto;
          overflow: visible;
        }

        .mg-youtube-gridline {
          stroke: rgba(255,255,255,0.075);
          stroke-width: 1;
        }

        .mg-youtube-date-line {
          stroke: rgba(255,255,255,0.035);
          stroke-width: 1;
        }

        .mg-youtube-axis-line {
          stroke: rgba(255,255,255,0.16);
          stroke-width: 1;
        }

        .mg-youtube-axis-label {
          fill: #8f98a7;
          font-size: 12px;
          font-weight: 800;
        }

        .mg-youtube-axis-label-date {
          font-size: 10px;
          fill: #9fb7c9;
        }

        .mg-youtube-bar {
          fill: url(#mgYoutubeBarFill);
          opacity: 0.78;
          transition: opacity 140ms ease, filter 140ms ease;
        }

        .mg-youtube-bar.compare {
          fill: #9aa3ad;
          opacity: 0.62;
        }

        .mg-youtube-bar.active {
          opacity: 1;
          filter: drop-shadow(0 0 9px rgba(255, 50, 50, 0.68));
        }

        .mg-youtube-bar.compare.active {
          filter: drop-shadow(0 0 9px rgba(180,190,200,0.55));
        }

        .mg-youtube-area-fill {
          opacity: 0.34;
          pointer-events: none;
        }

        .mg-youtube-area-fill.primary {
          fill: rgba(255,72,72,0.58);
        }

        .mg-youtube-area-fill.compare {
          fill: rgba(154,163,173,0.56);
        }

        .mg-youtube-area-line {
          fill: none;
          stroke-width: 2.4;
          opacity: 0.95;
          pointer-events: none;
        }

        .mg-youtube-area-line.primary {
          stroke: rgba(255,72,72,0.94);
        }

        .mg-youtube-area-line.compare {
          stroke: rgba(190,199,208,0.92);
        }

        .mg-youtube-cumulative-point {
          stroke: rgba(0,0,0,0.76);
          stroke-width: 1.5;
          opacity: 0.88;
          pointer-events: none;
        }

        .mg-youtube-cumulative-point.primary {
          fill: rgba(255,72,72,0.95);
        }

        .mg-youtube-cumulative-point.compare {
          fill: #9aa3ad;
        }

        .mg-youtube-cumulative-point.active {
          opacity: 1;
          filter: drop-shadow(0 0 8px rgba(255,255,255,0.42));
        }

        .mg-youtube-timeline-hitplane {
          cursor: pointer;
        }

        .mg-youtube-top-thumb-marker {
          pointer-events: none;
        }

        .mg-youtube-top-thumb-frame {
          fill: rgba(5,5,6,0.84);
          stroke: rgba(255,255,255,0.22);
          stroke-width: 1;
          filter: drop-shadow(0 10px 18px rgba(0,0,0,0.58));
        }

        .mg-youtube-top-thumb-frame.compare {
          stroke: rgba(180,190,200,0.55);
        }

        .mg-youtube-top-thumb-img {
          clip-path: inset(0 round 4px);
        }

        .mg-youtube-callout-leader {
          fill: none;
          stroke: rgba(255,72,72,0.72);
          stroke-width: 1.4;
          stroke-linecap: round;
          stroke-linejoin: round;
          stroke-dasharray: 4 4;
        }

        .mg-youtube-callout-leader.compare {
          stroke: rgba(190,200,212,0.68);
        }

        .mg-youtube-callout-anchor {
          fill: #ff4848;
          stroke: rgba(10,10,12,0.9);
          stroke-width: 1.6;
        }

        .mg-youtube-callout-anchor.compare {
          fill: #cfd8e3;
        }

        .mg-youtube-watermark {
          pointer-events: none;
        }

        .mg-youtube-watermark image {
          opacity: 0.30;
        }

        .mg-youtube-watermark-text {
          fill: rgba(255,255,255,0.26);
          font-size: 25px;
          font-weight: 900;
          letter-spacing: -0.02em;
        }

        .mg-youtube-timeline-tooltip {
          position: absolute;
          display: grid;
          grid-template-columns: 176px minmax(0, 1fr);
          gap: 14px;
          padding: 12px;
          border: 1px solid rgba(255,255,255,0.18);
          border-radius: 12px;
          background: rgba(7,7,8,0.94);
          box-shadow: 0 24px 70px rgba(0,0,0,0.70), 0 0 0 1px rgba(255,255,255,0.04) inset;
          backdrop-filter: blur(12px);
          pointer-events: none;
          z-index: 12;
        }

        .mg-youtube-tooltip-thumb {
          aspect-ratio: 16 / 9;
          border-radius: 7px;
          overflow: hidden;
          background: #111;
          border: 1px solid rgba(255,255,255,0.08);
        }

        .mg-youtube-tooltip-thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .mg-youtube-tooltip-body {
          min-width: 0;
        }

        .mg-youtube-tooltip-series {
          display: inline-flex;
          align-items: center;
          height: 20px;
          padding: 0 8px;
          margin-bottom: 7px;
          border-radius: 999px;
          color: #fff;
          background: rgba(255,72,72,0.18);
          border: 1px solid rgba(255,72,72,0.35);
          font-size: 11px;
          font-weight: 950;
        }

        .mg-youtube-tooltip-series.compare {
          background: rgba(154,163,173,0.14);
          border-color: rgba(180,190,200,0.36);
          color: #dce2e8;
        }

        .mg-youtube-tooltip-title {
          color: #fff;
          font-size: 15px;
          line-height: 1.22;
          font-weight: 950;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .mg-youtube-tooltip-channel {
          margin-top: 9px;
          color: #cfd8e3;
          font-size: 13px;
          font-weight: 850;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .mg-youtube-tooltip-meta {
          margin-top: 6px;
          color: #9ca3af;
          font-size: 12px;
          font-weight: 800;
        }

        .mg-youtube-period-slider-wrap {
          margin: 10px 72px 0 74px;
          padding: 6px 0 2px;
        }

        .mg-youtube-period-label-row {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          gap: 12px;
          color: #9fb7c9;
          font-size: 12px;
          font-weight: 800;
          margin-bottom: 6px;
        }

        .mg-youtube-period-label-row span:nth-child(2) {
          color: #cfd8e3;
          font-size: 11px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }

        .mg-youtube-period-label-row span:last-child {
          text-align: right;
        }

        .mg-youtube-mindshare-range-wrap {
          position: relative;
          height: 18px;
        }

        .mg-youtube-mindshare-range-track {
          position: absolute;
          left: 0;
          right: 0;
          top: 8px;
          height: 3px;
          background: rgba(255,255,255,0.09);
          border-radius: 999px;
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.03);
        }

        .mg-youtube-mindshare-range-fill {
          position: absolute;
          top: 8px;
          height: 3px;
          background: #9aa3ad;
          border-radius: 999px;
          box-shadow: 0 0 14px rgba(180,190,200,0.22);
        }

        .mg-mindshare-range {
          -webkit-appearance: none;
          appearance: none;
        }

        .mg-mindshare-range::-webkit-slider-runnable-track {
          height: 18px;
          background: transparent;
          border: none;
        }

        .mg-mindshare-range::-moz-range-track {
          height: 18px;
          background: transparent;
          border: none;
        }

        .mg-mindshare-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 28px;
          height: 14px;
          border-radius: 7px;
          border: none;
          background: #9aa3ad;
          cursor: grab;
          box-shadow: 0 0 10px rgba(180,190,200,0.28);
          pointer-events: auto;
          margin-top: 2px;
        }

        .mg-mindshare-range:hover::-webkit-slider-thumb {
          box-shadow: 0 0 10px rgba(180,190,200,0.28);
        }

        .mg-mindshare-range:active::-webkit-slider-thumb {
          cursor: grabbing;
        }

        .mg-mindshare-range::-moz-range-thumb {
          width: 28px;
          height: 14px;
          border-radius: 7px;
          border: none;
          background: #9aa3ad;
          cursor: grab;
          box-shadow: 0 0 10px rgba(180,190,200,0.28);
          pointer-events: auto;
        }

        .mg-youtube-range-input {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 18px;
          background: transparent;
          pointer-events: none;
          margin: 0;
        }

        .mg-youtube-timeline-skel-axis,
        .mg-youtube-timeline-skel-bottom,
        .mg-youtube-timeline-skel-bar {
          background-image: linear-gradient(90deg, rgba(255,255,255,0.055), rgba(255,255,255,0.12), rgba(255,255,255,0.055));
          background-size: 340px 100%;
          animation: mgYoutubeShimmer 1.35s linear infinite;
        }

        .mg-youtube-timeline-skel-axis {
          width: 190px;
          height: 13px;
          border-radius: 999px;
          margin: 5px 0 22px;
        }

        .mg-youtube-timeline-skel-bars {
          height: 390px;
          display: flex;
          align-items: flex-end;
          gap: 8px;
          padding: 0 18px;
          border-bottom: 1px solid rgba(255,255,255,0.12);
        }

        .mg-youtube-timeline-skel-bar {
          flex: 1;
          min-width: 3px;
          border-radius: 4px 4px 0 0;
        }

        .mg-youtube-timeline-skel-bottom {
          width: 70%;
          height: 12px;
          border-radius: 999px;
          margin: 20px auto 0;
        }

        .mg-youtube-state {
          min-height: 300px;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 10px;
          background: rgba(12,12,13,0.76);
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 28px;
          color: #aeb7c5;
          box-shadow: 0 16px 34px rgba(0,0,0,0.30);
        }

        .mg-youtube-state-title {
          color: #fff;
          font-size: 18px;
          font-weight: 950;
          margin-bottom: 7px;
        }

        .mg-youtube-state-copy {
          color: #9ca3af;
          font-size: 13px;
          font-weight: 700;
        }

        @container mg-viewport (max-width: 900px) {
          .mg-youtube-page { padding: 16px 14px 28px; }
          .mg-youtube-header { grid-template-columns: 1fr; align-items: stretch; }
          .mg-youtube-h1 { font-size: 28px; }
          .mg-youtube-stats { grid-template-columns: 1fr; }
          .mg-youtube-stats.compare { grid-template-columns: 1fr; }
          .mg-youtube-grid { grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 12px; }
          .mg-youtube-result-bar { align-items: flex-start; flex-direction: column; }
          .mg-youtube-results-row { align-items: stretch; flex-direction: column; }
          .mg-youtube-view-toggle { width: max-content; }
          .mg-youtube-timeline-compare-row { grid-template-columns: 1fr; }
          .mg-youtube-timeline-tools { grid-template-columns: 1fr; }
          .mg-youtube-timeline-panel { padding: 12px 10px 8px; }
          .mg-youtube-timeline-tooltip { position: static; width: auto; margin-top: 10px; grid-template-columns: 112px minmax(0, 1fr); }
          .mg-youtube-period-slider-wrap { margin-left: 0; margin-right: 0; }
          .mg-youtube-expanded-backdrop { padding: 16px; }
          .mg-youtube-expanded-player { width: min(100%, calc(var(--mg-vw) * 96)); }
        }
      `}</style>

      <div className="mg-youtube-shell">
        <header className="mg-youtube-header">
          <div className="mg-youtube-search-panel">
            <Search className="mg-youtube-search-icon" size={18} aria-hidden="true" />
            <TextInputField
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search video titles..."
              width="100%"
              aria-label="Search YouTube video titles"
              style={{
                height: 42,
                paddingLeft: 40,
                borderRadius: 8,
                background: "rgba(0,0,0,0.54)",
                borderColor: "rgba(255,255,255,0.14)",
                fontWeight: 750,
              }}
            />
          </div>
        <section className={showCompareStats ? "mg-youtube-stats compare" : "mg-youtube-stats"} aria-label="YouTube result totals">
          {showCompareStats ? (
            <>
              <div className="mg-youtube-stat primary">
                <div className="mg-youtube-stat-label">{timelinePayload.query || debouncedQuery} Matching Videos</div>
                <div className="mg-youtube-stat-value">
                  {timelineLoading ? "..." : fmtInt(timelinePayload.total)}
                </div>
              </div>
              <div className="mg-youtube-stat primary">
                <div className="mg-youtube-stat-label">{timelinePayload.query || debouncedQuery} Lifetime Views</div>
                <div className="mg-youtube-stat-value">
                  {timelineLoading ? "..." : fmtInt(timelinePayload.total_views)}
                </div>
              </div>
              <div className="mg-youtube-stat compare">
                <div className="mg-youtube-stat-label">{compareTimelinePayload.query || debouncedCompareQuery} Matching Videos</div>
                <div className="mg-youtube-stat-value">
                  {compareTimelineLoading ? "..." : fmtInt(compareTimelinePayload.total)}
                </div>
              </div>
              <div className="mg-youtube-stat compare">
                <div className="mg-youtube-stat-label">{compareTimelinePayload.query || debouncedCompareQuery} Lifetime Views</div>
                <div className="mg-youtube-stat-value">
                  {compareTimelineLoading ? "..." : fmtInt(compareTimelinePayload.total_views)}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="mg-youtube-stat">
                <div className="mg-youtube-stat-label">Matching Videos</div>
                <div className="mg-youtube-stat-value">
                  {summaryLoading ? "..." : fmtInt(summaryPayload.total)}
                </div>
              </div>
              <div className="mg-youtube-stat">
                <div className="mg-youtube-stat-label">Combined Lifetime Views</div>
                <div className="mg-youtube-stat-value">
                  {summaryLoading ? "..." : fmtInt(summaryPayload.total_views)}
                </div>
              </div>
            </>
          )}
        </section>
        </header>

        <div className="mg-youtube-results-row">
          <div className="mg-youtube-result-bar">
            <div>
              <strong>{viewMode === "thisWeek" ? "This Week" : resultLabel}</strong>
              {viewMode === "thisWeek"
                ? " timeline across the latest 7 days of available videos"
                : viewMode === "timeline" && hasSearch && debouncedCompareQuery
                ? ` timeline compared with "${compareTimelinePayload.query || debouncedCompareQuery}"`
                : viewMode === "timeline" && hasSearch
                ? ` timeline across all ${fmtInt(timelinePayload.total || payload.total)} matching videos`
                : hasSearch
                ? ` across ${fmtInt(payload.total)} matching videos`
                : " by lifetime views"}
            </div>
            <div>
              {viewMode === "thisWeek"
                ? `Showing all ${thisWeekLoading ? "..." : fmtInt(thisWeekPayload.videos.length)} this-week observations`
                : viewMode === "timeline"
                ? `Showing all ${timelineLoading || compareTimelineLoading ? "..." : fmtInt(timelineVideos.length + compareTimelineVideos.length)} timeline observations`
                : `Showing ${loading ? "..." : fmtInt(videos.length)} of ${loading ? "..." : fmtInt(payload.total)}`}
            </div>
          </div>

          <div className="mg-youtube-view-toggle" role="tablist" aria-label="YouTube view mode">
            <button
              type="button"
              className={viewMode === "cards" ? "active" : ""}
              onClick={() => setViewMode("cards")}
              aria-selected={viewMode === "cards"}
            >
              Cards
            </button>
            <button
              type="button"
              className={viewMode === "timeline" ? "active" : ""}
              onClick={() => hasSearch && setViewMode("timeline")}
              disabled={!hasSearch}
              aria-selected={viewMode === "timeline"}
              title={hasSearch ? "Show timeline" : "Search video titles to enable Timeline"}
            >
              Timeline
            </button>
            <button
              type="button"
              className={viewMode === "thisWeek" ? "active" : ""}
              onClick={() => setViewMode("thisWeek")}
              aria-selected={viewMode === "thisWeek"}
              title="Show videos released in the latest 7 days of available data"
            >
              This Week
            </button>
          </div>
        </div>

        {viewMode === "thisWeek" ? (
          <YouTubeTimeline
            videos={thisWeekPayload.videos}
            loading={thisWeekLoading}
            error={thisWeekError}
            query="This Week"
            total={thisWeekPayload.total}
            compareQuery=""
            comparePayload={{ query: "", total: 0, total_views: 0, videos: [] }}
            compareLoading={false}
            compareError=""
            onCompareChange={() => {}}
            showCompareInput={false}
            chartHeight={timelineChartHeight}
            onResizeStart={handleTimelineResizeStart}
            onExpand={setExpandedVideo}
            windowRange={timelineWindow}
            onWindowChange={setTimelineWindow}
          />
        ) : viewMode === "timeline" && hasSearch ? (
          <YouTubeTimeline
            videos={timelineVideos}
            loading={timelinePending}
            error={timelineError}
            query={debouncedQuery}
            total={timelinePayload.total || payload.total}
            compareQuery={compareSearchText}
            comparePayload={compareTimelinePayload}
            compareLoading={compareTimelineLoading}
            compareError={compareTimelineError}
            onCompareChange={setCompareSearchText}
            chartHeight={timelineChartHeight}
            onResizeStart={handleTimelineResizeStart}
            onExpand={setExpandedVideo}
            windowRange={timelineWindow}
            onWindowChange={setTimelineWindow}
          />
        ) : loading ? (
          <VideoSkeletonGrid />
        ) : error ? (
          <div className="mg-youtube-state" role="alert">
            <div>
              <div className="mg-youtube-state-title">Could not load videos</div>
              <div className="mg-youtube-state-copy">{error}</div>
            </div>
          </div>
        ) : videos.length === 0 ? (
          <div className="mg-youtube-state">
            <div>
              <div className="mg-youtube-state-title">No videos found</div>
              <div className="mg-youtube-state-copy">
                Try another title search to broaden the YouTube results.
              </div>
            </div>
          </div>
        ) : (
          <>
          <div className="mg-youtube-grid">
            {videos.map((video, index) => (
              <VideoCard
                key={video.video_id || index}
                video={video}
                index={index}
                onExpand={setExpandedVideo}
              />
            ))}
          </div>
          <div ref={loadMoreRef} className="mg-youtube-load-more" style={{ minHeight: 40, padding: "16px 0", color: "#9ca3af", textAlign: "center" }} aria-live="polite">
            {loadingMore ? "Loading more videos..." : null}
            {loadMoreError && <button type="button" onClick={() => setRetryPage((value) => value + 1)}>{loadMoreError} Retry</button>}
          </div>
          </>
        )}

        {expandedVideo && expandedVideoSrc && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`${expandedVideo.title || "YouTube"} expanded video`}
            className="mg-youtube-expanded-backdrop"
            onClick={() => setExpandedVideo(null)}
          >
            <div className="mg-youtube-expanded-player" onClick={(e) => e.stopPropagation()}>
              <iframe
                title={`${expandedVideo.title || "YouTube"} expanded video`}
                src={expandedVideoSrc}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="mg-youtube-frame"
              />
              <button
                type="button"
                className="mg-youtube-expanded-close"
                onClick={() => setExpandedVideo(null)}
                aria-label="Close expanded video"
                title="Close"
              >
                <CloseIcon size={20} strokeWidth={2.4} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default YouTube;